import React, { useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './SelectConnectableAccountsModal.module.scss'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { Text } from 'blaze-ui/Text/Text'
import { pluralize } from 'utils'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import cx from 'classnames'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { GoogleLogoFallback } from 'components/Previews/getIntegrationDetails'
import { FacebookPage } from 'api/clients/FacebookClient'
import {
  ExternalIntegration,
  ExternalIntegrationEnum,
  InstagramProfile,
  LinkedInProfile,
  XProfile,
} from 'api/clients/ExternalIntegrationsClient'
import { ConnectableAccount } from 'api/clients/IntegrationAuthsClient'
import Badge from 'common/Badge/Badge'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { noop } from 'lodash'

interface SelectConnectableAccountsModalProps {
  connectableAccounts: ConnectableAccount[]
  handleSubmit: (
    accounts: ConnectableAccount[],
    isRefreshOrOnCreated?: boolean | ((ids: number[]) => Promise<void>),
  ) => Promise<number[]>
  onCancel: () => void
  provider: ExternalIntegrationEnum
  isSingleSelect?: boolean
  allowReconnect?: boolean
}

interface ContentProps {
  connectableAccounts: ConnectableAccount[]
  provider: ExternalIntegrationEnum
  selectedAccounts: ConnectableAccount[]
  setSelectedAccounts: React.Dispatch<React.SetStateAction<ConnectableAccount[]>>
  isSingleSelect?: boolean
  allowReconnect?: boolean
}

// This is a temporary helper function to find the provider ID from the integration object
// it will be replaced by provider_id
export const findProviderId = (provider: ExternalIntegrationEnum, integration: ExternalIntegration) => {
  switch (provider) {
    case ExternalIntegrationEnum.Facebook:
      return (integration.additional_data as FacebookPage).id
    case ExternalIntegrationEnum.Instagram:
      return (integration.additional_data as InstagramProfile).id
    case ExternalIntegrationEnum.LinkedIn:
      return (integration.additional_data as LinkedInProfile).sub
    case ExternalIntegrationEnum.X:
      return (integration.additional_data as XProfile).id
    default:
      return null
  }
}

const getAccountDisplayName = (account: ConnectableAccount) =>
  account.additional_data?.username || account.name

const getFallbackAvatar = (provider: ExternalIntegrationEnum) => {
  if (provider === ExternalIntegrationEnum.GoogleBusinessProfile) return GoogleLogoFallback
  return Silhouette
}

export const SelectConnectableAccountsContent = ({
  connectableAccounts,
  provider,
  selectedAccounts,
  setSelectedAccounts,
  isSingleSelect = false,
  allowReconnect = false,
}: ContentProps) => {
  const { data } = useExternalIntegrations()
  const fallbackAvatar = getFallbackAvatar(provider)

  const alreadyConnectedAccounts = connectableAccounts.filter((connectableAccount) => {
    return data?.external_integrations.some((integration) => {
      return (
        integration.provider === provider &&
        (findProviderId(provider, integration) === connectableAccount.provider_id ||
          integration.provider_id === connectableAccount.provider_id)
      )
    })
  })

  if (isSingleSelect) {
    return (
      <div className={Styles.accountsList}>
        <RadioGroup
          aria-label="Title"
          value={selectedAccounts[0].provider_id}
          className={Styles.radioGroup}
          onChange={(selectedAccountProviderId) => {
            const newSelectedAccounts = connectableAccounts.filter(
              (account) => account.provider_id === selectedAccountProviderId,
            )
            setSelectedAccounts(newSelectedAccounts)
          }}
        >
          {connectableAccounts.map((account) => {
            const alreadyConnected =
              alreadyConnectedAccounts.some(
                (connectedAccount) => connectedAccount.provider_id === account.provider_id,
              ) && !allowReconnect

            return (
              <Radio
                className={Styles.radioOption}
                value={account.provider_id}
                hideRadio
                key={account.provider_id}
                isDisabled={alreadyConnected}
              >
                {(radioBulletProps) => (
                  <div
                    key={account.provider_id}
                    className={cx(Styles.accountItem, alreadyConnected && Styles.disabled)}
                  >
                    <div>
                      <RadioBullet {...radioBulletProps} size={20} />
                    </div>
                    <img
                      src={account.picture || fallbackAvatar}
                      alt="profile"
                      className={cx(Styles.avatar, alreadyConnected && Styles.disabled)}
                      onError={(e) => {
                        e.currentTarget.src = fallbackAvatar
                      }}
                    />
                    <Text variant="largeList" color={alreadyConnected ? 'var(--neutral-30)' : undefined}>
                      {getAccountDisplayName(account)}
                    </Text>
                  </div>
                )}
              </Radio>
            )
          })}
        </RadioGroup>
      </div>
    )
  }

  return (
    <div className={Styles.accountsList}>
      {connectableAccounts.map((account) => {
        const alreadyConnected =
          alreadyConnectedAccounts.some((connectedAccount) => connectedAccount.provider_id === account.provider_id) &&
          !allowReconnect

        return (
          <div key={account.provider_id} className={cx(Styles.accountItem, alreadyConnected && Styles.disabled)}>
            <Checkbox
              size="lg"
              isDisabled={alreadyConnected}
              aria-label={`Select account ${getAccountDisplayName(account)}`}
              isSelected={selectedAccounts.includes(account)}
              onChange={(checked) => {
                const updatedAccounts = checked
                  ? [...selectedAccounts, account]
                  : selectedAccounts.filter((selectedaccount) => selectedaccount.provider_id !== account.provider_id)
                setSelectedAccounts(updatedAccounts)
              }}
            />
            <img
              src={account.picture || fallbackAvatar}
              alt="profile"
              className={cx(Styles.avatar, alreadyConnected && Styles.disabled)}
              onError={(e) => {
                e.currentTarget.src = fallbackAvatar
              }}
            />
            <div className={Styles.accountDetails}>
              <div className={Styles.accountName}>
                <Text variant="largeList" color={alreadyConnected ? 'var(--neutral-30)' : undefined}>
                  {getAccountDisplayName(account)}
                </Text>
                {!allowReconnect &&
                  alreadyConnectedAccounts.some(
                    (connectedAccount) => connectedAccount.provider_id === account.provider_id,
                  ) && <Badge>Connected</Badge>}
              </div>
              <Text variant="placeholder">ID: {account.provider_id}</Text>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const SelectConnectableAccountsModal: React.FC<SelectConnectableAccountsModalProps> = ({
  connectableAccounts,
  handleSubmit,
  onCancel,
  provider,
  isSingleSelect = false,
  allowReconnect = false,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [selectedAccounts, setSelectedAccounts] = useState<Array<ConnectableAccount>>(
    isSingleSelect ? [connectableAccounts[0]] : [],
  )
  const { closeModal } = useModals()

  const title = showConfirmation
    ? 'Are you sure you want to leave the integration setup?'
    : `Select which ${pluralize('account', isSingleSelect ? 1 : 2)} to connect`

  return (
    <Modal.Root aria-label={title} onPressOutside={noop}>
      <Modal.Header
        title={
          <>
            {showConfirmation && <Modal.BackButton onPress={() => setShowConfirmation(false)} />}
            {title}
          </>
        }
      />
      <Modal.Content>
        {showConfirmation ? (
          <Text>Leaving now will cancel any progress and your integration will NOT be connected.</Text>
        ) : (
          <SelectConnectableAccountsContent
            selectedAccounts={selectedAccounts}
            setSelectedAccounts={setSelectedAccounts}
            connectableAccounts={connectableAccounts}
            provider={provider}
            isSingleSelect={isSingleSelect}
            allowReconnect={allowReconnect}
          />
        )}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="lg" variant="tertiary" onPress={() => setShowConfirmation(!showConfirmation)}>
            {showConfirmation ? 'No, Return to Setup' : 'Cancel Setup'}
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            size="lg"
            isDisabled={!selectedAccounts.length}
            onPress={() => {
              if (showConfirmation) {
                onCancel()
                closeModal()
              } else {
                handleSubmit(selectedAccounts)
                closeModal()
              }
            }}
          >
            {showConfirmation
              ? 'Yes, Cancel Setup'
              : `Connect ${selectedAccounts.length || ''} ${pluralize('account', selectedAccounts.length)}`}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
